import React from 'react';
import { Form, Field } from 'react-final-form';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import css from './Step1Company.module.css';

const INDUSTRIES = [
  'Electronics', 'Furniture', 'Textile & Apparel', 'Machinery',
  'Automotive Parts', 'Chemical', 'Food & Beverage', 'Packaging',
  'Plastic & Rubber', 'Metal & Steel', 'Medical Devices', 'Toys & Games',
  'Sports & Outdoor', 'Cosmetics & Beauty', 'Construction Materials',
];

const EXPERIENCE_OPTIONS = [
  'Less than 1 year', '1–2 years', '3–5 years', '6–10 years', 'More than 10 years',
];

const ToggleRow = ({ label, name, description }) => (
  <Field name={name} type="checkbox">
    {({ input }) => (
      <label style={{
        display: 'flex',
        alignItems: 'center',
        gap: 14,
        padding: '16px 20px',
        background: input.checked ? 'linear-gradient(135deg,#eff6ff,#dbeafe)' : '#ffffff',
        border: `1.5px solid ${input.checked ? '#2563eb' : '#e2e8f0'}`,
        borderRadius: 12,
        cursor: 'pointer',
        transition: 'all 0.2s',
        marginBottom: 10,
        boxShadow: input.checked ? '0 2px 8px rgba(37,99,235,0.12)' : '0 1px 3px rgba(0,0,0,0.04)',
      }}>
        {/* Custom toggle */}
        <div style={{
          width: 44,
          height: 24,
          borderRadius: 99,
          background: input.checked ? '#2563eb' : '#cbd5e1',
          position: 'relative',
          flexShrink: 0,
          transition: 'background 0.2s',
        }}>
          <div style={{
            position: 'absolute',
            top: 3,
            left: input.checked ? 23 : 3,
            width: 18,
            height: 18,
            borderRadius: '50%',
            background: '#ffffff',
            boxShadow: '0 1px 4px rgba(0,0,0,0.2)',
            transition: 'left 0.2s',
          }} />
          <input {...input} type="checkbox" style={{ position: 'absolute', opacity: 0, width: 0, height: 0 }} />
        </div>
        <div>
          <div style={{ fontSize: 14, fontWeight: 700, color: input.checked ? '#1d4ed8' : '#0f172a' }}>{label}</div>
          {description && <div style={{ fontSize: 12, color: '#64748b', marginTop: 2, lineHeight: 1.5 }}>{description}</div>}
        </div>
      </label>
    )}
  </Field>
);

const AgentStep3Services = ({ nextStep, prevStep, initialValues }) => {
  const validate = values => {
    const errors = {};
    if (!values.industriesServed || values.industriesServed.length === 0) {
      errors.industriesServed = 'Select at least one industry';
    }
    if (!values.yearsOfExperience) errors.yearsOfExperience = 'Please select your experience level';
    return errors;
  };

  const onSubmit = values => nextStep(values);

  return (
    <Form
      onSubmit={onSubmit}
      validate={validate}
      initialValues={initialValues}
      render={({ handleSubmit, values, form, submitFailed, errors }) => (
        <form onSubmit={handleSubmit} className={css.form}>

          <div className={css.header}>
            <span className={css.step}>Step 3 of 5</span>
            <h2 className={css.title}>Services & Capabilities</h2>
            <p className={css.description}>
              Tell buyers what industries you serve and what services you offer.
            </p>
          </div>

          <hr className={css.divider} />

          {/* ── Industries Served ── */}
          <section className={css.section}>
            <h3 className={css.sectionTitle}>Industries Served</h3>
            <Field name="industriesServed">
              {({ meta }) => (
                <>
                  <div style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '8px',
                    padding: '14px',
                    background: '#f8fafc',
                    border: `1.5px solid ${meta.touched && meta.error ? '#ef4444' : '#e2e8f0'}`,
                    borderRadius: '12px',
                  }}>
                    {INDUSTRIES.map(ind => {
                      const selected = (values.industriesServed || []).includes(ind);
                      return (
                        <button
                          key={ind}
                          type="button"
                          onClick={() => {
                            const current = values.industriesServed || [];
                            const next = selected
                              ? current.filter(i => i !== ind)
                              : [...current, ind];
                            form.change('industriesServed', next);
                          }}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 6,
                            padding: '6px 14px',
                            borderRadius: 999,
                            border: `1.5px solid ${selected ? '#2563eb' : '#cbd5e1'}`,
                            background: selected ? 'linear-gradient(135deg,#2563eb,#1d4ed8)' : '#ffffff',
                            color: selected ? '#ffffff' : '#475569',
                            fontSize: 13,
                            fontWeight: selected ? 700 : 500,
                            cursor: 'pointer',
                            transition: 'all 0.18s',
                            boxShadow: selected ? '0 2px 8px rgba(37,99,235,0.25)' : 'none',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {selected && <span style={{ fontSize: 11 }}>✓</span>}
                          {ind}
                        </button>
                      );
                    })}
                  </div>
                  {(values.industriesServed || []).length > 0 && (
                    <div style={{ fontSize: 12, color: '#2563eb', fontWeight: 600, marginTop: 6 }}>
                      {(values.industriesServed || []).length} industr{(values.industriesServed || []).length !== 1 ? 'ies' : 'y'} selected
                    </div>
                  )}
                  {meta.touched && meta.error && <span className={css.error}>{meta.error}</span>}
                </>
              )}
            </Field>
          </section>

          {/* ── Experience ── */}
          <section className={css.section}>
            <h3 className={css.sectionTitle}>Years of Experience</h3>
            <div className={css.fieldFull}>
              <Field name="yearsOfExperience">
                {({ input, meta }) => (
                  <>
                    <div style={{ position: 'relative' }}>
                      <select
                        {...input}
                        style={{
                          width: '100%',
                          height: 52,
                          padding: '0 44px 0 16px',
                          border: `1.5px solid ${meta.touched && meta.error ? '#ef4444' : '#e2e8f0'}`,
                          borderRadius: 12,
                          fontSize: 15,
                          fontWeight: 500,
                          color: input.value ? '#0f172a' : '#94a3b8',
                          background: '#f8fafc',
                          appearance: 'none',
                          WebkitAppearance: 'none',
                          outline: 'none',
                          cursor: 'pointer',
                          boxSizing: 'border-box',
                          boxShadow: meta.touched && meta.error ? '0 0 0 3px rgba(239,68,68,0.1)' : 'none',
                          transition: 'border-color 0.2s, box-shadow 0.2s',
                        }}
                      >
                        <option value="">Select experience level</option>
                        {EXPERIENCE_OPTIONS.map(opt => (
                          <option key={opt} value={opt}>{opt}</option>
                        ))}
                      </select>
                      {/* Custom arrow */}
                      <div style={{
                        position: 'absolute',
                        right: 16,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        pointerEvents: 'none',
                        color: '#64748b',
                        fontSize: 18,
                      }}>▾</div>
                    </div>
                    {meta.touched && meta.error && <span className={css.error}>{meta.error}</span>}
                  </>
                )}
              </Field>
            </div>
          </section>

          {/* ── Services Offered ── */}
          <section className={css.section}>
            <h3 className={css.sectionTitle}>Services Offered</h3>
            <ToggleRow
              name="warehouseAvailability"
              label="Warehouse Availability"
              description="You have access to warehouse facilities for storage and consolidation."
            />
            <ToggleRow
              name="customsAssistance"
              label="Customs Assistance"
              description="You help buyers navigate customs clearance and import/export documentation."
            />
            <ToggleRow
              name="sampleHandling"
              label="Sample Handling"
              description="You can arrange and manage product samples before bulk orders."
            />
            <ToggleRow
              name="doorToDoorService"
              label="Door-to-Door Service"
              description="You provide end-to-end logistics from factory to buyer's destination."
            />
          </section>

          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 28, gap: 12 }}>
            <button
              type="button"
              onClick={prevStep}
              style={{
                padding: '13px 24px',
                border: '1.5px solid #e2e8f0',
                borderRadius: 12,
                background: '#ffffff',
                color: '#475569',
                fontSize: 14,
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'all 0.2s',
              }}
            >
              ← Previous
            </button>
            <button
              type="submit"
              style={{
                padding: '13px 28px',
                border: 'none',
                borderRadius: 12,
                background: 'linear-gradient(135deg,#2563eb,#1d4ed8)',
                color: '#ffffff',
                fontSize: 14,
                fontWeight: 700,
                cursor: 'pointer',
                boxShadow: '0 4px 14px rgba(37,99,235,0.3)',
                transition: 'all 0.25s',
              }}
            >
              Next : Agreements →
            </button>
          </div>

        </form>
      )}
    />
  );
};

AgentStep3Services.defaultProps = { initialValues: {} };
AgentStep3Services.propTypes = {
  nextStep: PropTypes.func.isRequired,
  prevStep: PropTypes.func.isRequired,
  initialValues: PropTypes.object,
};

export default AgentStep3Services;
